import { IGame } from "../../../core/engine/types/game";
import Game from "../../../core/engine/game";
import RemotePlayer from "../../../core/engine/models/impl/remote-player";
import {
  Packet,
  PlayerLinesPacket,
  PlayerMovePacket,
} from "../../../core/protocol/packets";
import { PacketTypes, PlayerState } from "../../../core/protocol/types";
import InitialState from "../../../core/engine/states/initial-state";
import Executor from "../../../core/engine/executor/executor";
import { IMultiplayerAdapter } from "../../../core/engine/interfaces/IMultiplayerAdapter";
import { CanvasStatus } from "../../../core/engine/types/stats";

export interface RemoteGameEntry {
  game: IGame;
  connectionId?: string;
}

/**
 * Simple adapter for remote games that provides read-only game state
 * Remote games don't send packets, so they only need getGameMode() and getSyncpoint()
 */
class RemoteGameAdapter implements IMultiplayerAdapter {
  constructor(
    private getGameModeFn: () => string,
    private getSyncpointFn: () =>
      | "playing"
      | "waitForWinner"
      | "waitForRestart"
      | "last"
      | undefined
  ) {}

  getGameMode():
    | "standard"
    | "survivor"
    | "sprint"
    | "hot_potato"
    | "team"
    | undefined {
    const mode = this.getGameModeFn();
    if (
      mode === "standard" ||
      mode === "survivor" ||
      mode === "sprint" ||
      mode === "hot_potato" ||
      mode === "team"
    ) {
      return mode;
    }
    return "standard";
  }

  getSyncpoint():
    | "playing"
    | "waitForWinner"
    | "waitForRestart"
    | "last"
    | undefined {
    return this.getSyncpointFn();
  }
}

/**
 * Manages remote Game instances for other players
 * Reference: quadra/source/net_list.cc (Net_list class)
 */
export default class RemoteGameManager {
  private remoteGames: Map<number, RemoteGameEntry> = new Map();
  private connectionToPlayer: Map<string, number> = new Map();
  private currentGameMode: string = "standard";
  private currentSyncpoint:
    | "playing"
    | "waitForWinner"
    | "waitForRestart"
    | "last"
    | undefined = "playing";

  /**
   * Create or update remote game for a player
   * Reference: quadra/source/net_list.cc:278-314 (Net_list::add_player)
   * @param connectionId Connection ID
   * @param playerState Player state from network
   * @param gameSeed Game seed for deterministic pieces
   * @param waitDownload Whether to wait for P_DOWNLOAD (true for initial, false for mid-game join)
   * @param gameMode Game mode (standard, survivor, etc.)
   */
  createOrUpdateRemoteGame(
    connectionId: string,
    playerState: PlayerState,
    gameSeed: number,
    waitDownload: boolean = true,
    gameMode?: string
  ): IGame {
    if (gameMode) {
      this.currentGameMode = gameMode;
    }

    const numPlayer = playerState.numPlayer;
    if (numPlayer === undefined) {
      throw new Error(
        `[RemoteGameManager.createOrUpdateRemoteGame] Cannot create remote game without numPlayer (connectionId=${connectionId}, name=${playerState.name})`
      );
    }

    const existingEntry = this.remoteGames.get(numPlayer);
    if (existingEntry) {
      if (connectionId) {
        this.updateConnectionMapping(
          numPlayer,
          connectionId,
          existingEntry.connectionId
        );
        existingEntry.connectionId = connectionId;
      }
      // Update mutable properties on existing game
      if (existingEntry.game.canvas) {
        if (playerState.team !== undefined) {
            (existingEntry.game.canvas as any).color = playerState.team;
        }
        if (playerState.handicap !== undefined) {
            (existingEntry.game.canvas as any).handicap = playerState.handicap;
        }
        if (playerState.level !== undefined) {
            existingEntry.game.canvas.level = playerState.level;
        }
      }
      return existingEntry.game;
    }

    const player = new RemotePlayer([]);
    // Create a minimal mock audio manager since remote games don't play audio directly
    const mockAudioManager = {
        playSound: (name: string, playbackRate?: number): any => null,
        setGlobalVolume: (volume: number): void => {},
        setSfxVolume: (volume: number): void => {},
        preloadGameSounds: (): void => {},
        loadSound: (name: string, path: string): void => {}
    } as unknown as import("../../../core/engine/types/audio-manager").IAudioManager;
    const game = new Game(player, mockAudioManager, gameSeed);
    game.isRemote = true;
    game.remotePlayerId = connectionId;
    game.canvas.numPlayer = numPlayer;
    game.canvas.smooth = true;
    game.canvas.shadow = true;
    game.canvas.horizontalRepeat = 2;
    game.canvas.verticalRepeat = 2;
    game.canvas.level = playerState.level ?? 1;
    game.blockPack = playerState.blockPack || "default";
    game.canvasPack = playerState.canvasPack || "default";

    if ((game.canvas as any).color !== undefined) {
      (game.canvas as any).color = playerState.team;
    }

    if ((game.canvas as any).handicap !== undefined) {
      (game.canvas as any).handicap = playerState.handicap ?? 0;
    }

    const adapter = new RemoteGameAdapter(
      () => this.currentGameMode,
      () => this.currentSyncpoint
    );
    (game as Game).setMultiplayerAdapter(adapter);

    game.canvas.waitDownload = waitDownload;
    const mainExecutor = new Executor();
    game.mainExecutor = mainExecutor;
    const initialState = new InitialState(game);

    initialState.enter();
    mainExecutor.add(initialState);
    game.overmind.start(mainExecutor);

    if (initialState.isFirstTime()) {
      initialState.markFirstTimeComplete();
    }

    this.remoteGames.set(numPlayer, { game, connectionId });
    if (connectionId) {
      this.connectionToPlayer.set(connectionId, numPlayer);
    }

    return game;
  }

  /**
   * Update game mode for all remote games
   * Should be called when game mode changes (e.g., when game starts)
   * Restarts remote games so they can reinitialize with correct idle state
   */
  setGameMode(gameMode: string): void {
    const oldMode = this.currentGameMode;
    this.currentGameMode = gameMode;

    if (oldMode !== gameMode && this.remoteGames.size > 0) {
      console.log(
        `[RemoteGameManager.setGameMode] Restarting ${this.remoteGames.size} remote games due to mode change`
      );
      for (const [, entry] of this.remoteGames.entries()) {
        const game = entry.game;
        game.canvas.restart();

        const executor = new Executor();
        const initialState = new InitialState(game);
        initialState.enter();
        executor.add(initialState);
        if (initialState.isFirstTime()) {
          initialState.markFirstTimeComplete();
        }
        game.mainExecutor = executor;
        game.overmind.start(executor);
      }
    }
  }

  /**
   * Update syncpoint for all remote games
   * Should be called when syncpoint changes
   */
  setSyncpoint(
    syncpoint:
      | "playing"
      | "waitForWinner"
      | "waitForRestart"
      | "last"
      | undefined
  ): void {
    this.currentSyncpoint = syncpoint;
  }

  /**
   * Process packet for remote player
   * Reference: quadra/source/net_list.cc (routes packets to Canvas)
   *
   * CRITICAL: P_DOWNLOAD packets are handled via watchers and NOT queued (matches original).
   * In original C++, P_DOWNLOAD is NOT in the list of packets that go to game->stackpacket().
   * It goes directly to Player_init::net_call() via watchers and is deleted immediately.
   */
  processPacketForPlayer(numPlayer: number, packet: Packet): void {
    const entry = this.remoteGames.get(numPlayer);
    if (!entry) {
      console.warn(
        `[RemoteGameManager.processPacketForPlayer] No game for numPlayer=${numPlayer}, ignoring packet ${packet.packetId}`
      );
      return;
    }

    const label =
      entry.connectionId !== undefined
        ? `connectionId=${entry.connectionId}`
        : `numPlayer=${numPlayer}`;

    if (packet.packetId === PacketTypes.DOWNLOAD_STATE) {
      const watchers = (entry.game as any).packetWatchers?.get(
        PacketTypes.DOWNLOAD_STATE
      );
      if (watchers && watchers.size > 0) {
        watchers.forEach((watcher: any) => watcher.onPacket(packet));
      }

      return;
    }

    console.log(
      `[RemoteGameManager.processPacketForPlayer] Queuing packet ${
        packet.packetId
      } (${PacketTypes[packet.packetId]}) for ${label} to game's packet queue`
    );

    entry.game.stackPacket(packet);
  }

  processPacket(connectionId: string, packet: Packet): void {
    const numPlayer = this.connectionToPlayer.get(connectionId);
    if (numPlayer === undefined) {
      console.warn(
        `[RemoteGameManager.processPacket] Connection ${connectionId} not mapped to a numPlayer, ignoring packet ${packet.packetId}`
      );
      return;
    }
    this.processPacketForPlayer(numPlayer, packet);
  }

  /**
   * Queue move packet to remote game
   * Reference: quadra/source/net_server.cc:566-583 (clientmoves)
   */
  queueMovePacketForPlayer(
    numPlayer: number,
    movePacket: PlayerMovePacket
  ): void {
    const entry = this.remoteGames.get(numPlayer);
    if (entry && entry.game.stackPacket) {
      entry.game.stackPacket(movePacket);
    } else {
      console.warn(
        `[RemoteGameManager.queueMovePacketForPlayer] No remote game found for numPlayer=${numPlayer} (packet numPlayer=${
          movePacket.numPlayer
        }), available games: ${Array.from(this.remoteGames.keys()).join(", ")}`
      );
    }
  }

  queueMovePacket(connectionId: string, movePacket: PlayerMovePacket): void {
    const numPlayer = this.connectionToPlayer.get(connectionId);
    if (numPlayer === undefined) {
      console.warn(
        `[RemoteGameManager.queueMovePacket] Connection ${connectionId} not mapped to a numPlayer (packet numPlayer=${movePacket.numPlayer})`
      );
      return;
    }
    this.queueMovePacketForPlayer(numPlayer, movePacket);
  }

  /**
   * Get remote game for a connection
   */
  getRemoteGame(connectionId: string): IGame | undefined {
    const numPlayer = this.connectionToPlayer.get(connectionId);
    if (numPlayer === undefined) {
      return undefined;
    }
    return this.remoteGames.get(numPlayer)?.game;
  }

  getRemoteGameByNumPlayer(numPlayer: number): IGame | undefined {
    return this.remoteGames.get(numPlayer)?.game;
  }

  /**
   * Get all remote games
   */
  getAllRemoteGames(): Map<number, RemoteGameEntry> {
    return this.remoteGames;
  }

  /**
   * Remove remote game when player leaves
   * Reference: quadra/source/net_list.cc:316-326 (Net_list::remove_player)
   */
  removeRemoteGame(connectionId: string): void {
    const numPlayer = this.connectionToPlayer.get(connectionId);
    if (numPlayer === undefined) {
      return;
    }
    this.removeRemoteGameByNumPlayer(numPlayer);
  }

  removeRemoteGameByNumPlayer(numPlayer: number): void {
    const entry = this.remoteGames.get(numPlayer);
    if (!entry) {
      return;
    }
    entry.game.stop();
    this.remoteGames.delete(numPlayer);
    if (entry.connectionId) {
      this.connectionToPlayer.delete(entry.connectionId);
    }
  }

  /**
   * Update canvas state for a remote game (matches original P_STATE handling)
   */
  updateCanvasState(
    numPlayer: number,
    canvasState: "playing" | "waitForWinner" | "waitForRestart" | "last"
  ): void {
    const entry = this.remoteGames.get(numPlayer);
    if (!entry) {
      return;
    }

    const canvas = entry.game.canvas;
    switch (canvasState) {
      case "waitForWinner":
        canvas.status = CanvasStatus.WAITFORWINNER;
        canvas.idle = 2;
        break;
      case "waitForRestart":
        canvas.status = CanvasStatus.WAITFORRESTART;
        canvas.idle = 2;
        break;
      case "last":
        canvas.status = CanvasStatus.LAST;
        break;
      case "playing":
      default:
        canvas.status = CanvasStatus.PLAYING;
        canvas.idle = 1;
        break;
    }
  }

  /**
   * Process line attacks BEFORE stepping (critical order)
   * Reference: quadra/source/net_list.cc:405-410
   * Original: while((p = (Packet_lines *)game->peekpacket(P_LINES))) { sendlines(p); game->removepacket(); }
   */
  processLineAttacks(): void {
    for (const [, entry] of this.remoteGames.entries()) {
      let linePacket: PlayerLinesPacket | null;
      while (
        (linePacket = entry.game.peekPacket(
          PacketTypes.PLAYER_LINES
        ) as PlayerLinesPacket | null)
      ) {
        this.applyLineAttack(entry.game, linePacket);

        entry.game.removePacket();
      }
    }
  }

  /**
   * Apply line attack to remote game
   * Reference: quadra/source/net_list.cc:102-110 (sendlines calls c->add_packet)
   * Matches how local player handles it in Game.tsx:260-267
   */
  private applyLineAttack(game: IGame, packet: PlayerLinesPacket): void {
    const attack = packet.attack;

    const attackerColor = 7;

    const holePositions = attack.holePositions || [];

    game.canvas.addPacket(
      attackerColor,
      attack.lines,
      attack.complexity,
      attack.type,
      holePositions,
      packet.sender !== undefined && packet.sender !== 255
        ? packet.sender
        : undefined,
      attack.param
    );
  }

  /**
   * Step all remote game state machines
   * Reference: quadra/source/net_list.cc:412-417
   * Original: c->over->step() for each Canvas (both local and remote)
   */
  stepAll(): void {
    for (const [, entry] of this.remoteGames.entries()) {
      entry.game.overmind.step();
    }
  }

  /**
   * Set paused state for all remote games
   * Match original: game->paused only affects move processing, NOT overmind stepping
   * Reference: quadra/source/player.cc:447 - Player_process_key checks game->paused but still steps
   * Reference: quadra/source/player.cc:1317 - Player_wait_block checks game->paused but still steps
   * The overmind continues stepping during countdown, allowing state transitions
   */
  setPaused(paused: boolean): void {
    for (const [, entry] of this.remoteGames.entries()) {
      entry.game.paused = paused;
    }
  }

  /**
   * Clean up all remote games
   */
  cleanup(): void {
    for (const [, entry] of this.remoteGames.entries()) {
      entry.game.stop();
    }
    this.remoteGames.clear();
    this.connectionToPlayer.clear();
  }

  /**
   * Add a remote game (for migration from another RemoteGameManager)
   */
  addRemoteGame(numPlayer: number, game: IGame, connectionId?: string): void {
    const previousEntry = this.remoteGames.get(numPlayer);
    if (previousEntry?.connectionId) {
      this.connectionToPlayer.delete(previousEntry.connectionId);
    }
    this.remoteGames.set(numPlayer, { game, connectionId });
    if (connectionId) {
      this.connectionToPlayer.set(connectionId, numPlayer);
    }
  }

  private updateConnectionMapping(
    numPlayer: number,
    newConnectionId?: string,
    oldConnectionId?: string
  ): void {
    if (oldConnectionId && oldConnectionId !== newConnectionId) {
      this.connectionToPlayer.delete(oldConnectionId);
    }
    if (newConnectionId) {
      this.connectionToPlayer.set(newConnectionId, numPlayer);
    }
  }

  /**
   * Update player team for a remote game
   */
  updatePlayerTeam(numPlayer: number, team: number): void {
    const entry = this.remoteGames.get(numPlayer);
    if (entry && entry.game.canvas) {
      (entry.game.canvas as any).color = team;
    }
  }
}
